import {Field, useField} from "formik";
import FormikCurrencyInput from "./FormikCurrencyInput";
import type {Field as FieldType} from "@types/Field";

import CalendarInputIcon from "../../../assets/media/svg/icons/calendar-input.svg";


/**
 * Renders a generic input field using Formik.
 * @param {FieldType} formField - Field configuration object.
 * @param {string} [extraClass=""] - Additional CSS classes.
 * @returns {JSX.Element} - A Formik Field component.
 */
const renderInputField = (formField: FieldType, extraClass = "") => (
    <Field
        component={
            formField.icon === "currency" ? FormikCurrencyInput : "input"
        }
        data-clinic-name={formField.clinicName}
        type={formField.type}
        name={formField.name}
        placeholder={formField.placeholder}
        id={formField.name}
        className={
            formField.icon
                ? `with-icon icon-${formField.icon} ${extraClass}`
                : extraClass
        }
        step={formField.step}
        max={formField.max}
    />
);

/**
 * Renders a date field.
 * @param {FieldType} formField - Field configuration object.
 * @returns {JSX.Element} - A Formik Field component for dates.
 */
const renderDateField = (formField: FieldType): JSX.Element => (
    <div className="custom-month-wrapper">
        <Field
            type="month"
            name={formField.name}
            id={formField.name}
            data-clinic-name={formField.clinicName}
            className={`without-icon ${formField.className || ""}`}
            placeholder={formField.placeholder}
        />
        {/* Custom calendar icon */}
        <span className="custom-calendar-icon"><CalendarInputIcon/></span>
    </div>
);


/**
 * Renders a map field with autocomplete functionality.
 * Includes hidden fields for latitude and longitude.
 * @param {FieldType} formField - Field configuration object.
 * @returns {JSX.Element} - A Formik Field component for maps.
 */
const renderMapField = (formField: FieldType) => {
    const latField = formField.latField ?? `${formField.name}_lat`;
    const lonField = formField.lonField ?? `${formField.name}_lon`;
    return (
        <>
            <Field
                type="text"
                name={formField.name}
                id={formField.name}
                data-clinic-name={formField.clinicName}
                data-module="maps-autocomplete"
                data-lat-field={latField}
                data-lon-field={lonField}
                placeholder={formField.placeholder}
            />
            <Field type="hidden" name={latField}/>
            <Field type="hidden" name={lonField}/>
        </>
    );
};

/**
 * Renders a checkbox field.
 * @param {FieldType} formField - Field configuration object.
 * @returns {JSX.Element} - A Formik Field component for checkboxes.
 */
const renderCheckboxField = (formField: FieldType): JSX.Element => (
    <>
        <Field type="checkbox" name={formField.name} id={formField.name}
               data-clinic-name={formField.clinicName}/>
        <label htmlFor={formField.name}>{formField.label}</label>
    </>
);

/**
 * Renders a group of radio buttons.
 * @param {FieldType} formField - Field configuration object.
 * @returns {JSX.Element} - A collection of Formik Field components for radio buttons.
 */
const renderRadioField = (formField: FieldType) =>
    formField.options?.map((option, i) => (
        <div
            className="tool-form__form__field__input__radio"
            key={`${formField.name}-${i}`}>
            <Field
                type="radio"
                name={formField.name}
                data-clinic-name={formField.clinicName}
                value={option.value}
                id={`${formField.name}-${i}`}
            />
            <label htmlFor={`${formField.name}-${i}`}>{option.label}</label>
        </div>
    ));

/**
 * Renders a hidden field.
 * @param {FieldType} formField - Field configuration object.
 * @returns {JSX.Element} - A Formik Field component for hidden inputs.
 */
const renderHiddenField = (formField: FieldType) => (
    <Field type="hidden" name={formField.name} id={formField.name}
           data-clinic-name={formField.clinicName}/>
);

/**
 * Renders a select dropdown field.
 * @param {FieldType} formField - Field configuration object.
 * @returns {JSX.Element} - A Formik Field component for dropdowns.
 */
const renderSelectField = (formField: FieldType) => (
    <Field
        as="select"
        name={formField.name}
        data-module="dropdown"
        data-dropdown={formField.name}
        data-clinic-name={formField.clinicName}
        className="simple-dropdown">
        {!formField.defaultValue && <option value="">Selecionar</option>}
        {formField.options?.map((option) => (
            <option value={option.value} key={option.value}>
                {option.label}
            </option>
        ))}
    </Field>
);

/**
 * Renders a textarea field.
 * @param {FieldType} formField - Field configuration object.
 * @returns {JSX.Element} - A Formik Field component for textareas.
 */
const renderTextareaField = (formField: FieldType) => (
    <Field
        as="textarea"
        name={formField.name}
        id={formField.name}
        data-clinic-name={formField.clinicName}
        placeholder={formField.placeholder}
        className="tool-form__textarea"
    />
);

/**
 * Renders a range slider field.
 * @param {FieldType} formField - Field configuration object.
 * @returns {JSX.Element} - A Formik Field component for range sliders.
 */
const renderRangeField = (formField: FieldType) => {
    const [field, meta, helpers] = useField(formField.name);
    const [min, max, step] = formField.options?.map(opt => Number(opt.value)) || [];
    const [minLabel, maxLabel, stepLabel] = formField.options?.map(opt => opt.label) || [];

    return (
        <div className="tool-form__range">
            <div className="tool-form__range__input">
                <label htmlFor={formField.name}>{formField.label}</label>

                <div className="tool-form__range__labels">
                    <span>{minLabel ?? 0}</span>
                    <span>{maxLabel ?? 100}</span>
                </div>

                <input
                    type="range"
                    name={formField.name}
                    id={formField.name}
                    data-clinic-name={formField.clinicName}
                    min={min ?? 0}
                    max={max ?? 100}
                    step={step ?? 1}
                    value={field.value}
                    onChange={e => helpers.setValue(Number(e.target.value))}
                    className="tool-form__range__slider"
                    aria-label={formField.label}
                />
            </div>
            <span className="tool-form__range__value">
  {stepLabel === "€"
      ? new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(field.value)
      : `${field.value} ${stepLabel}`}
</span>
        </div>
    );
};

/**
 * Renders a file upload field.
 * @param {FieldType} formField - Field configuration object.
 * @returns {JSX.Element} - A Formik Field component for file uploads.
 */
const renderFileField = (formField: FieldType) => (
    <Field
        type="file"
        name={formField.name}
        id={formField.name}
        data-clinic-name={formField.clinicName}
        className="tool-form__file-input"
    />
);

/**
 * Renders a password field.
 * @param {FieldType} formField - Field configuration object.
 * @returns {JSX.Element} - A Formik Field component for passwords.
 */
const renderPasswordField = (formField: FieldType) => (
    <Field
        type="password"
        name={formField.name}
        id={formField.name}
        data-clinic-name={formField.clinicName}
        placeholder={formField.placeholder}
        className="tool-form__password-input"
    />
);

/**
 * Renders a color picker field.
 * @param {FieldType} formField - Field configuration object.
 * @returns {JSX.Element} - A Formik Field component for color pickers.
 */
const renderColorPickerField = (formField: FieldType) => (
    <Field
        type="color"
        name={formField.name}
        id={formField.name}
        data-clinic-name={formField.clinicName}
        className="tool-form__color-picker"
    />
);

/**
 * Renders a multi-select dropdown field.
 * @param {FieldType} formField - Field configuration object.
 * @returns {JSX.Element} - A Formik Field component for multi-selects.
 */
const renderMultiSelectField = (formField: FieldType) => (
    <Field
        as="select"
        name={formField.name}
        id={formField.name}
        data-clinic-name={formField.clinicName}
        multiple
        className="tool-form__multi-select">
        {formField.options?.map((option) => (
            <option value={option.value} key={option.value}>
                {option.label}
            </option>
        ))}
    </Field>
);

/**
 * A map associating field types to their respective render functions.
 */
const fieldRenderers: Record<string, (formField: FieldType) => JSX.Element> = {
    number: renderInputField,
    text: renderInputField,
    tel: renderInputField,
    email: renderInputField,
    date: renderDateField,
    map: renderMapField,
    checkbox: renderCheckboxField,
    radio: renderRadioField,
    hidden: renderHiddenField,
    select: renderSelectField,
    textarea: renderTextareaField,
    range: renderRangeField,
    file: renderFileField,
    password: renderPasswordField,
    color: renderColorPickerField,
    multiselect: renderMultiSelectField,
};

/**
 * Renders the appropriate field based on its type.
 * @param {FieldType} formField - Field configuration object.
 * @returns {JSX.Element} - The rendered field or a fallback message.
 */
const renderFieldType = (formField: FieldType) => {
    const render = fieldRenderers[formField.type];
    return render ? render(formField) : <span>No type set yet...</span>;
};

export default renderFieldType;